import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  id?: number;
  name: string;
  description: string;
  provider: string;
  host: string;
  port: number;
  username: string;
  password: string;
  senderEmail: string;
  senderName: string;
  useTls: boolean;
  useSsl: boolean;
  priority: number;
  dailyLimit: number;
  hourlyLimit: number;
  status: string;
}

const EMAIL_PROVIDERS = [
  { label: "SMTP", value: "SMTP" },
  { label: "SendGrid", value: "SENDGRID" },
  { label: "Mailgun", value: "MAILGUN" },
  { label: "AWS SES", value: "AWS_SES" },
  { label: "Postmark", value: "POSTMARK" },
  { label: "Brevo", value: "BREVO" },
];

const GATEWAY_STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Deactivated", value: "Deactivated" },
];

const EmailGatewayFormDialog = (props: ModalType) => {
    const [record, setRecord] = useState<RecordType | null>(props?.record || null);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [passwordMasked, setPasswordMasked] = useState<boolean>(!!props?.record?.id);
    const message = useRef<any>();

    useEffect(() => {
      const isEdit = !!props?.record?.id;
      setPasswordMasked(isEdit);
      setRecord(
        props?.record
          ? {
              id: props.record.id,
              name: props.record.name || "",
              description: props.record.description || "",
              provider: props.record.provider || "SMTP",
              host: props.record.host || "",
              port: props.record.port || 587,
              username: props.record.username || "",
              password: props.record.password || "",
              senderEmail: props.record.senderEmail || "",
              senderName: props.record.senderName || "",
              useTls: props.record.useTls !== false,
              useSsl: props.record.useSsl || false,
              priority: props.record.priority || 1,
              dailyLimit: props.record.dailyLimit || 10000,
              hourlyLimit: props.record.hourlyLimit || 1000,
              status: props.record.status || "Active",
            }
          : {
              id: undefined,
              name: "",
              description: "",
              provider: "SMTP",
              host: "",
              port: 587,
              username: "",
              password: "",
              senderEmail: "",
              senderName: "",
              useTls: true,
              useSsl: false,
              priority: 1,
              dailyLimit: 10000,
              hourlyLimit: 1000,
              status: "Active",
            }
      );
    }, [props?.record]);

  const clearForm = () => {
    setRecord(null);
  };

  const validateForm = () => {
    if (!record?.name || !record?.senderEmail) {
      MessageUtils.showErrorMessage(message, "Please fill in name and sender email");
      return false;
    }
    const isNew = !props?.record?.id;
    if (isNew && !record?.password) {
      MessageUtils.showErrorMessage(message, "Please fill in password (API key / SMTP password)");
      return false;
    }
    const hostRequiredProviders = ["SMTP"];
    if (hostRequiredProviders.includes(record?.provider) && (!record?.host || !record?.port || !record?.username)) {
      MessageUtils.showErrorMessage(message, "Please fill in SMTP host, port, and username");
      return false;
    }
    return true;
  };

  const saveGateway = () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    const payload = { ...(record as any) };
    const isEdit = !!props?.record?.id;
    const url = isEdit ? `/api/v1/email-gateways/${props?.record.id}` : "/api/v1/email-gateways";
    const request = isEdit
      ? new BaseApiServiceImpl(url).putRequestWithJsonResponse(payload)
      : new BaseApiServiceImpl(url).postRequestWithJsonResponse(payload);
    request
      .then(() => {
        setIsSaving(false);
        MessageUtils.showSuccessMessage(props?.messageRef, isEdit ? "Email gateway updated" : "Email gateway created");
        props?.reloadFn();
        clearForm();
        props.toggle();
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  return (
    <Dialog
      header={props?.record?.id ? "Edit Email Gateway" : "Create Email Gateway"}
      visible={props.isOpen}
      onHide={() => {
        clearForm();
        props.toggle();
      }}
      breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      style={{ width: "50vw" }}
    >
      <Messages ref={message} />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-12 md:col-6">
          <label htmlFor="name">Name</label>
          <InputText
            id="name"
            value={record?.name || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, name: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="provider">Provider</label>
          <Dropdown
            id="provider"
            value={record?.provider || "SMTP"}
            options={EMAIL_PROVIDERS}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, provider: e.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="host">Host</label>
          <InputText
            id="host"
            value={record?.host || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, host: e.target.value }))}
            className="w-full mt-1"
            placeholder="smtp.gmail.com"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="port">Port</label>
          <InputText
            id="port"
            type="number"
            value={record?.port || 587}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, port: parseInt(e.target.value) || 587 }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="username">Username</label>
          <InputText
            id="username"
            value={record?.username || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, username: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="password">Password {passwordMasked && <small className="text-secondary">(leave empty to keep current)</small>}</label>
          <Password
            id="password"
            value={passwordMasked ? "••••••••" : (record?.password || "")}
            onChange={(e) => {
              setPasswordMasked(false);
              setRecord((prevState: any) => ({ ...prevState, password: e.target.value }));
            }}
            onFocus={() => {
              if (passwordMasked) {
                setPasswordMasked(false);
              }
            }}
            className="w-full mt-1"
            feedback={false}
            placeholder={passwordMasked ? "••••••••" : ""}
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="senderEmail">Sender Email</label>
          <InputText
            id="senderEmail"
            value={record?.senderEmail || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, senderEmail: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="senderName">Sender Name</label>
          <InputText
            id="senderName"
            value={record?.senderName || ""}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, senderName: e.target.value }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="priority">Priority</label>
          <InputText
            id="priority"
            type="number"
            value={record?.priority || 1}
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, priority: parseInt(e.target.value) || 1 }))}
            className="w-full mt-1"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="status">Status</label>
          <Dropdown
            id="status"
            value={record?.status || "Active"}
            options={GATEWAY_STATUS_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setRecord((prevState: any) => ({ ...prevState, status: e.value }))}
            className="w-full mt-1"
          />
        </div>
      </div>
      <div className="flex justify-content-end mt-4 gap-2">
        <Button label="Cancel" icon="pi pi-times" onClick={() => { clearForm(); props.toggle(); }} className="p-button-text" />
        <Button label="Save" icon="pi pi-check" onClick={saveGateway} loading={isSaving} />
      </div>
    </Dialog>
  );
};

export default EmailGatewayFormDialog;
